import type React from 'react';
import type { Dayjs } from 'dayjs';
import type { SelectOptionType } from '../Controls/Select/types';
import type { SingleSelectProps } from '../Controls/Select/SingleSelect';
import type { MultiSelectProps } from '../Controls/Select/MultiSelect';
import type { SearchProps } from '../Controls/Input/SearchInput';
import type { SegmentedProps } from '../Controls/Segmented';
import type { NumberInputProps } from '../Controls/Input/NumberInput';

// ---------- Value shape ----------

export type FilterValues = Record<string, unknown>;

export type DateRangeValue = [Dayjs | null, Dayjs | null] | null;
export type NumericRangeValue = [number | null, number | null] | null;

// ---------- DependsOn ----------

export interface DependsOnEntry {
  name: string;
  onParentChange?: 'reset' | 'reset-to-default' | 'preserve-if-valid';
}

// ---------- DataSource ----------

export type StaticDataSource<TOption extends SelectOptionType = SelectOptionType> = {
  type: 'static';
  options: TOption[];
};

export type AsyncDataSource<TOption extends SelectOptionType = SelectOptionType> = {
  type: 'async';
  load: (deps: FilterValues, signal: AbortSignal) => Promise<TOption[]>;
  cacheKey?: (deps: FilterValues) => string;
};

export type DataSource<TOption extends SelectOptionType = SelectOptionType> =
  | StaticDataSource<TOption>
  | AsyncDataSource<TOption>;

// ---------- FilterItemBase ----------

export type FilterSize = 'small' | 'middle' | 'large';

export interface FilterItemBase<TValue = unknown> {
  name: string;
  label: string;
  placeholder?: string;

  /**
   * Bar-presence flag. When true, the pill is always visible and cannot be
   * removed via the `+` menu. Does NOT imply anything about value validity —
   * see `clearable` for that. Default: false (removable).
   */
  pinned?: boolean;

  /**
   * Value-empty validity flag. When false, the field's value must always be
   * set to something non-empty; the in-popover clear-X is hidden. Default: true.
   * Requires `pinned: true` AND a default source (`defaultValue` or
   * `getDefaultValue`) when set to false — see invariants.
   */
  clearable?: boolean;

  /**
   * Synchronous static default. Used as:
   *   - the initial seed when no other source provides a value;
   *   - the restore target for non-clearable fields on `clear` / `clearAll`
   *     (when set; otherwise `getDefaultValue` is rerun);
   *   - the restore target for any field on `reset` / `resetAll` (when set;
   *     otherwise `getDefaultValue` is rerun).
   * Not used for clearable fields on `clear` / `clearAll` — those go to
   * canonical empty.
   */
  defaultValue?: TValue;

  /**
   * Async/dynamic default. Runs AFTER options resolve. Used when the default
   * depends on loaded options or other filter values.
   *
   * Precedence: if both `defaultValue` and `getDefaultValue` are set,
   * `defaultValue` is used for initial seed and is the restore target for
   * `clear` / `clearAll` (non-clearable) / `reset` / `resetAll`. The role of
   * `getDefaultValue` is to provide an async/options-derived value when no
   * static `defaultValue` is configured.
   */
  getDefaultValue?: (
    options: SelectOptionType[],
    currentValues: FilterValues,
  ) => TValue | null | Promise<TValue | null>;

  dependsOn?: DependsOnEntry[];
  position?: number;
  size?: FilterSize;
  debounceMs?: number;
  excludeFromSummary?: boolean;
  sharedKey?: string;
}

// ---------- Per-variant configs ----------

export type FilterControlPassthroughCommon = {
  'data-testid'?: string;
  className?: string;
  disabled?: boolean;
};

export interface SelectFilterConfig<TValue = string | number> extends FilterItemBase<TValue | null> {
  type: 'SELECT';
  dataSource: DataSource;
  searchable?: boolean;
  getValueFromOption?: (option: SelectOptionType) => TValue;
  getLabelFromOption?: (option: SelectOptionType) => React.ReactNode;
  controlProps?: Partial<Omit<SingleSelectProps, 'value' | 'onChange' | 'options' | 'multiple'>>
    & FilterControlPassthroughCommon;
}

export interface SelectMultiFilterConfig<TValue = string | number>
  extends FilterItemBase<TValue[]> {
  type: 'SELECT_MULTI';
  dataSource: DataSource;
  searchable?: boolean;
  showSelectAll?: boolean;
  minSelections?: number;
  controlProps?: Partial<Omit<MultiSelectProps, 'value' | 'onChange' | 'options' | 'multiple'>>
    & FilterControlPassthroughCommon;
}

export interface TextSearchFilterConfig extends FilterItemBase<string> {
  type: 'TEXT_SEARCH';
  minLength?: number;
  caseInsensitive?: boolean;
  controlProps?: Partial<Omit<SearchProps, 'value' | 'onChange'>> & FilterControlPassthroughCommon;
}

/**
 * SEGMENT_TOGGLE. All `FilterItemBase` flags apply with the standard defaults
 * (`pinned: false`, `clearable: true`). Typical primary-toggle usage
 * (always-visible, must-have-a-value) is declared explicitly at the call site
 * as `pinned: true, clearable: false, defaultValue: ...`.
 *
 * Options come from a `dataSource` — same shape as SELECT / SELECT_MULTI.
 * Static `{ type: 'static', options: [...] }` covers the typical fixed-segment
 * case; async `{ type: 'async', load: ..., cacheKey: ... }` covers
 * dynamic-segment scenarios (segments loaded per plan, per role, etc.).
 *
 * `getDefaultValue` is supported and receives the loaded options just like for
 * SELECT — enabling baselines such as "the first available segment."
 */
export interface SegmentToggleFilterConfig<TValue = string | number>
  extends FilterItemBase<TValue> {
  type: 'SEGMENT_TOGGLE';
  dataSource: DataSource;
  controlProps?: Partial<Omit<SegmentedProps, 'value' | 'onChange' | 'options'>>
    & FilterControlPassthroughCommon;
}

export interface DateRangePreset {
  label: string;
  value: [Dayjs, Dayjs] | (() => [Dayjs, Dayjs]);
}

export interface DateRangeFilterConfig extends FilterItemBase<DateRangeValue> {
  type: 'DATE_RANGE';
  presets?: DateRangePreset[];
  minDate?: Dayjs;
  maxDate?: Dayjs;
  format?: string;
  disabledDate?: (current: Dayjs) => boolean;
  controlProps?: FilterControlPassthroughCommon;
}

export type NumericRangeFormat = 'number' | 'currency' | 'percent';

export interface NumericRangeFilterConfig extends FilterItemBase<NumericRangeValue> {
  type: 'NUMERIC_RANGE';
  min?: number;
  max?: number;
  step?: number;
  format?: NumericRangeFormat;
  currency?: string;
  controlProps?: FilterControlPassthroughCommon & {
    minProps?: Partial<Omit<NumberInputProps, 'value' | 'onChange'>>;
    maxProps?: Partial<Omit<NumberInputProps, 'value' | 'onChange'>>;
  };
}

export type FilterItemConfig =
  | SelectFilterConfig
  | SelectMultiFilterConfig
  | TextSearchFilterConfig
  | SegmentToggleFilterConfig
  | DateRangeFilterConfig
  | NumericRangeFilterConfig;

export type FilterItemType = FilterItemConfig['type'];

// ---------- Phases ----------

export type FilterFieldPhase = 'IDLE' | 'LOADING_OPTIONS' | 'LOADING_DEFAULT' | 'READY' | 'ERROR';

// ---------- Events ----------

export type FilterChangeSource = 'user' | 'cascade' | 'imperative';

export type FilterChangeMeta = {
  trigger?: string;
  affected: string[];
  prevValues: FilterValues;
  source: FilterChangeSource;
  partialErrors?: string[];
};

export interface FilterBarPropsEvents<T extends FilterValues = FilterValues> {
  onInit?: (initialValues: T) => void;
  onChange?: (values: T, meta: FilterChangeMeta) => void;
  onFieldChange?: Partial<Record<keyof T & string,
    (value: unknown, meta: FilterChangeMeta) => void>>;
}

// ---------- Controller ----------

export interface FilterBarPersistenceController {
  save: () => Promise<void>;
  load: () => Promise<void>;
  clear: () => Promise<void>;
  isDirty: boolean;
}

export interface FilterBarController<T extends FilterValues = FilterValues> {
  values: T;
  isReady: boolean;
  isLoading: (name: keyof T & string) => boolean;
  isLoadingAny: boolean;
  visibleFields: string[];
  items: FilterItemConfig[];

  getOptions: (name: keyof T & string) => SelectOptionType[];
  getSelectedOptions: (name: keyof T & string) => SelectOptionType[];
  findOption: (name: keyof T & string, value: unknown) => SelectOptionType | undefined;

  /** Set a single field. Always cascades. */
  set: (name: keyof T & string, value: unknown) => void;
  /** Set many fields in one transaction. Default `cascade: true`. */
  setMany: (patch: Partial<T>, options?: { cascade?: boolean }) => void;

  /**
   * Per-field "clear":
   *   - clearable field → canonical empty (`null` / `[]` / `''`).
   *   - non-clearable field → restore baseline (`defaultValue` else rerun
   *     `getDefaultValue` against current options).
   * Clears `userTouched`. Cascades to dependents through normal rules.
   */
  clear: (name: keyof T & string) => void;

  /**
   * Apply the per-field `clear` rule to every field, in a single transaction.
   * Non-clearable fields restore baseline; clearable fields go to canonical
   * empty. Single onChange after the whole cascade settles.
   */
  clearAll: () => void;

  /**
   * Per-field "reset to baseline": restore the field to its declared default
   * regardless of `clearable` — `defaultValue` if set, else rerun
   * `getDefaultValue` against current options. Clears `userTouched`. Cascades.
   */
  reset: (name: keyof T & string) => void;

  /**
   * Apply the per-field `reset` rule to every field, in a single transaction.
   * Returns the bar to its declared baseline (effectively a re-init, modulo
   * persistence).
   */
  resetAll: () => void;

  /** Re-fetch options for one or all fields (bypasses cacheKey). */
  reload: (name?: keyof T & string) => void;

  addField: (name: keyof T & string) => void;
  removeField: (name: keyof T & string) => void;

  getPhase: (name: keyof T & string) => FilterFieldPhase;
  getError: (name: keyof T & string) => string | null;

  persistence?: FilterBarPersistenceController;
}

// ---------- Persistence ----------

export interface PersistenceAdapter {
  load: () => FilterValues | null | Promise<FilterValues | null>;
  save: (values: FilterValues) => void | Promise<void>;
  clear?: () => void | Promise<void>;
  debounceMs?: number;
  exclude?: string[];
}

export interface BuiltinAdapterOptions {
  tenantId: string;
  userId: string;
  scope: string;
  debounceMs?: number;
  exclude?: string[];
}

// ---------- Labels (i18n hook) ----------

export interface FilterBarLabels {
  clearAll?: string;
  resetAll?: string;
  addFilter?: string;
  searchPlaceholder?: string;
  loadFailed?: string;
  reload?: string;
  barAriaLabel?: string;
  removeFromBar?: string;
  clearValue?: string;
  filtersLabel?: string;
}

export const DEFAULT_FILTER_BAR_LABELS: Required<FilterBarLabels> = {
  clearAll: 'Clear All',
  resetAll: 'Reset',
  addFilter: 'Add filter',
  searchPlaceholder: 'Search…',
  loadFailed: 'Failed to load options',
  reload: 'Reload',
  barAriaLabel: 'Filters',
  removeFromBar: 'Remove from bar',
  clearValue: 'Clear value',
  filtersLabel: 'Filters:',
};

// ---------- Column Filter Sync (R2 — types only) ----------

export interface ColumnFilterSync {
  columnFilters: Record<string, unknown>;
  onColumnFilterChange: (columnKey: string, value: unknown) => void;
  subscribe?: (cb: (filters: Record<string, unknown>) => void) => () => void;
  fieldToColumnMap?: Record<string, string>;
}

// ---------- Operator envelope (advanced rules — type guard only) ----------

export interface FilterOperatorValue<TValue = unknown> {
  _op: 'eq' | 'neq' | 'contains' | 'in' | 'nin' | 'between' | 'gt' | 'lt';
  value: TValue;
}

export function isFilterOperatorValue(v: unknown): v is FilterOperatorValue {
  if (typeof v !== 'object' || v === null) return false;
  const obj = v as Record<string, unknown>;
  if (typeof obj._op !== 'string') return false;
  return ['eq', 'neq', 'contains', 'in', 'nin', 'between', 'gt', 'lt'].includes(obj._op);
}

// ---------- FilterBar props ----------

export interface FilterBarProps<T extends FilterValues = FilterValues>
  extends FilterBarPropsEvents<T> {
  items: FilterItemConfig[];

  values?: T;
  defaultValues?: Partial<T>;

  controller?: FilterBarController<T>;

  size?: FilterSize;

  addable?: boolean;
  availableToAdd?: string[];
  initialVisibleFields?: string[];

  showClearAll?: boolean;
  showResetAll?: boolean;

  persistence?: PersistenceAdapter;
  autoSave?: boolean;

  isLoadingExternal?: boolean;

  leading?: React.ReactNode;
  trailing?: React.ReactNode;

  labels?: FilterBarLabels;

  columnFilterSync?: ColumnFilterSync;

  className?: string;
}

export interface UseFilterBarConfig<T extends FilterValues = FilterValues>
  extends FilterBarPropsEvents<T> {
  items: FilterItemConfig[];
  initialValues?: Partial<T>;
  values?: T;
  persistence?: PersistenceAdapter;
  autoSave?: boolean;
  initialVisibleFields?: string[];
}
